/**
 * Context Menu Component
 * A simple right-click context menu for nodes
 */

import { escapeHtml, escapeAttr } from "../../../engine/core/validation.js";

let menuEl = null;
let currentNodeId = null;
let menuItems = [];

/**
 * Initialize the context menu
 * Call this once on app startup
 */
export function init() {
  if (menuEl) return;

  menuEl = document.createElement("div");
  menuEl.className = "context-menu";
  menuEl.style.display = "none";
  document.body.appendChild(menuEl);

  // Close menu on click outside
  document.addEventListener("click", (e) => {
    if (!menuEl.contains(e.target)) {
      hide();
    }
  });

  // Close menu on escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      hide();
    }
  });

  // Close menu on scroll
  document.addEventListener("scroll", event => {
    if (!menuEl.contains(event.target)) hide();
  }, true);
  menuEl.addEventListener('keydown', event => {
    event.stopPropagation();
    if (event.key === 'Escape') { event.preventDefault(); hide(); return; }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const items = [...menuEl.querySelectorAll('.context-menu-item')];
    let index = items.indexOf(document.activeElement);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = items.length - 1;
    else index = (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[index]?.focus();
  });
}

/**
 * Register menu items
 * @param {Array<{id: string, label: string|Function, icon?: string, shortcut?: string|Function, condition?: (nodeId) => boolean, action: (nodeId) => void}>} items
 */
export function registerItems(items) {
  menuItems = items;
}

/**
 * Show the context menu at position
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {string} nodeId - The node ID that was right-clicked
 * @param {Object} context - Additional context (e.g., node data)
 */
export function show(x, y, nodeId, context = {}) {
  if (!menuEl) init();

  currentNodeId = nodeId;

  // Filter items based on condition. In empty-space mode (right-click on
  // canvas/background, not on a specific node) we only show items that
  // explicitly opt in via `availableInEmptySpace`, so the menu doesn't try
  // to "Edit" or "Delete" a node that wasn't right-clicked on.
  const isEmptySpace = !!context?.onEmptySpace;
  const visibleItems = menuItems.filter((item) => {
    if (isEmptySpace) {
      if (item.separator) return false; // structural separators don't make sense in the short menu
      if (!item.availableInEmptySpace) return false;
    }
    if (item.condition) {
      return item.condition(nodeId, context);
    }
    return true;
  });

  if (visibleItems.length === 0) {
    return; // Don't show empty menu
  }

  // Optional header (used in empty-space mode to clarify "where Paste lands"
  // — bubble view doesn't render the root node, so users would otherwise
  // have no visual cue for the destination).
  const headerHtml = context?.headerLabel
    ? `<div class="context-menu-header">${escapeHtml(context.headerLabel)}</div>`
    : "";

  // Build menu HTML
  menuEl.innerHTML = headerHtml + visibleItems
    .map((item) => {
      if (item.separator) {
        return '<div class="context-menu-separator"></div>';
      }
      const icon = item.icon
        ? `<span class="context-menu-icon">${escapeHtml(item.icon)}</span>`
        : "";
      const dangerClass = item.danger ? " danger" : "";
      const shortcut = typeof item.shortcut === 'function' ? item.shortcut(nodeId, context) : item.shortcut;
      // Labels may be functions so they are translated when the menu opens.
      const label = typeof item.label === 'function' ? item.label(nodeId, context) : item.label;
      const hint = shortcut ? `<kbd class="context-menu-shortcut">${escapeHtml(shortcut)}</kbd>` : '';
      return `<button type="button" class="context-menu-item${dangerClass}" data-id="${escapeAttr(item.id)}">${icon}<span class="context-menu-label">${escapeHtml(label)}</span>${hint}</button>`;
    })
    .join("");

  // Attach click handlers
  menuEl.querySelectorAll(".context-menu-item").forEach((itemEl) => {
    itemEl.addEventListener("click", (e) => {
      e.stopPropagation();
      const itemId = itemEl.dataset.id;
      const item = menuItems.find((i) => i.id === itemId);
      const targetId = currentNodeId;
      hide();
      if (item && item.action) {
        item.action(targetId, context);
      }
    });
  });

  // Position the menu
  menuEl.style.display = "block";

  // Adjust position to keep menu in viewport
  const rect = menuEl.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let finalX = x;
  let finalY = y;

  if (x + rect.width > viewportWidth) {
    finalX = viewportWidth - rect.width - 10;
  }
  if (y + rect.height > viewportHeight) {
    finalY = viewportHeight - rect.height - 10;
  }

  menuEl.style.left = `${Math.max(8, finalX)}px`;
  menuEl.style.top = `${Math.max(8, finalY)}px`;
}

/**
 * Hide the context menu
 */
function hide() {
  if (menuEl) {
    menuEl.style.display = "none";
  }
  currentNodeId = null;
}
